/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { AutocompleteChips, Button, Spinner } from '../../../shared/components';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { OperationMissionForm } from '../../../services/types/Missions';
import Context from '../../App/MainProvider/context';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import userService from '../../../services/userService';
import reducer, { SearchUserAction } from './UserQueryReducer';
import { User } from '../../../services/types/User';

interface Props {
  handleSubmit: (values: OperationMissionForm, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  formState?: OperationMissionForm;
  isSubmitting: boolean;
}

const missionFormSchema = Yup.object().shape({
  startDate: Yup.date().required('required'),
  endDate: Yup.date().required('required').min(Yup.ref('startDate')),
  country: Yup.string().required('required'),
  destinationCountry: Yup.string().required('required'),
  objective: Yup.string().required('required'),
  members: Yup.array().min(1, 'You must select at least one member').required('required'),
});

const MissionForm = (props: Props) => {
  const { handleSubmit, handleCancel, formState, isSubmitting } = props;
  const { t } = useTranslation();
  const { countries } = useContext(Context);
  const [members, loadingMembers, , dispatch] = useServiceReducer(userService.getUsers, reducer, {
    query: '',
    convergence: true,
    includeGS: false,
  });
  const formInit: OperationMissionForm = {
    missionNumber: formState ? formState.missionNumber : '',
    startDate: formState ? formState.startDate : null,
    endDate: formState ? formState.endDate : null,
    country: formState ? formState.country : '',
    destinationCountry: formState ? formState.destinationCountry : '',
    objective: formState ? formState.objective : '',
    members: formState ? formState.members : [],
  };

  const handleEgsMembersSearch = (egsMemberQuery: string) => {
    const action = {
      type: 'QUERY_USER',
      query: egsMemberQuery,
      convergence: true,
    } as SearchUserAction;
    dispatch(action);
  };

  const handleEgsMembersChange = (formik: FormikProps<OperationMissionForm>, selected: User[]) => {
    const formattedEgsMembers = selected.map((user) => ({
      email: user.email,
      fullName: user.fullName,
    }));
    formik.setFieldValue('members', formattedEgsMembers);
  };

  return (
    <Formik
      initialValues={formInit}
      validationSchema={missionFormSchema}
      validateOnMount
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as OperationMissionForm;
        handleSubmit(payload, resetForm);
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form
          className="form mission-form"
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
        >
          <div className="modal__body">
            <div className="task-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('missionNumber')}</label>
                  <Field id="missionNumber" name="missionNumber" disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-6">
                  <label className="col-form-label">{t('startDate')}</label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper">
                      <DatePicker
                        id="startDate"
                        name="startDate"
                        selected={formik.values.startDate}
                        onChange={(date: Date | null) => formik.setFieldValue('startDate', date)}
                        dateFormat="dd/MM/yyy"
                        className="form-control-date-picker"
                        autoComplete="off"
                        placeholderText="DD/MM/YYYY"
                        minDate={new Date()}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label">{t('endDate')}</label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper">
                      <DatePicker
                        id="endDate"
                        name="endDate"
                        selected={formik.values.endDate}
                        onChange={(date: Date | null) => formik.setFieldValue('endDate', date)}
                        dateFormat="dd/MM/yyy"
                        className="form-control-date-picker"
                        autoComplete="off"
                        placeholderText="DD/MM/YYYY"
                        minDate={new Date()}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-6">
                  <label className="col-form-label">{t('country')}</label>
                  <div className="select-wrapper">
                    <Field as="select" id="country" name="country">
                      <option value="">{t('selectOption')}</option>
                      {countries.map((x) => (
                        <option value={x}>{t(`countryCode.${x}`)}</option>
                      ))}
                    </Field>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label">{t('destinationCountry')}</label>
                  <div className="select-wrapper">
                    <Field as="select" id="destinationCountry" name="destinationCountry">
                      <option value="">{t('selectOption')}</option>
                      {countries.map((x) => (
                        <option value={x}>{t(`countryCode.${x}`)}</option>
                      ))}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label">{t('missionObjective')}</label>
                  <Field
                    className="scrollarea"
                    as="textarea"
                    id="objective"
                    name="objective"
                    placeholder="Mission Objectives"
                    maxLength={1000}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <AutocompleteChips
                    labelKey="fullName"
                    htmlId="egs-members-typeahead"
                    text={t('missionMembers')}
                    placeholder="Ex. Emma Jackson"
                    onSearch={handleEgsMembersSearch}
                    onChange={(users: User[]) => handleEgsMembersChange(formik, users)}
                    list={members}
                    loading={loadingMembers}
                    render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                    multiple
                    selected={formik.values.members}
                    className="col-form-label"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <hr />
            <Spinner loading={isSubmitting} />
            <Button
              key="btnCancel"
              className="btn-tertiary"
              text={t('cancel')}
              type="reset"
              disabled={isSubmitting}
            />
            <Button
              key="btnSave"
              className="btn-primary"
              text={t(formState && formState.missionId ? 'updateMission' : 'createMission')}
              type="submit"
              disabled={isSubmitting || !formik.isValid}
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default MissionForm;
